import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { CrmObjectService } from './crm-object-service/crm-object.service';
import { GalaxyFilterDefinitionInterface, GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { ContactTableCustomizationService } from '../table-customization/contact-table-customization.service';
import { CompanyTableCustomizationService } from '../table-customization/company-table-customization.service';
import { ActivityCustomizationService } from '../table-customization/activity-customization.service';
import {
  CrmInjectionToken,
  ActivityType,
  CrmDependencies,
  ObjectType,
  TaskTableCustomizationServiceToken,
} from '../../tokens-and-interfaces';
import { FilterType } from '@vendasta/crm';
import { convertToGalaxyFilterOperators, convertToGalaxyFilterType } from '../../utils';
import { ActivityTableCustomizationService } from '../../shared-services/table-customization/_abstract-table-customization.service';
import { OpportunityTableCustomizationService, CustomObjectTableCustomizationService } from '../table-customization';

// right now filters are sorted manually in the backend, the page size needs to cover the amount of system + standard + extensions + custom filters (up to 100)
const FILTER_PAGE_SIZE = 200;

function deduplicateFilters(filters: GalaxyFilterInterface[]): GalaxyFilterInterface[] {
  return filters.filter((filter, index) => {
    return index === filters.findIndex((f) => filter.filterId === f.filterId);
  });
}

@Injectable()
export class CrmFiltersService {
  private initialFilters$$ = new BehaviorSubject<GalaxyFilterInterface[]>([]);
  initialFilters$ = this.initialFilters$$.asObservable();
  private filtersRequestMap = new Map<string, Observable<GalaxyFilterDefinitionInterface[]>>();
  private fullFiltersRequestMap = new Map<string, Observable<GalaxyFilterDefinitionInterface[]>>();

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmObjectService: CrmObjectService,
    private readonly contactTableCustomization: ContactTableCustomizationService,
    private readonly companyTableCustomization: CompanyTableCustomizationService,
    private readonly activityCustomizationService: ActivityCustomizationService,
    private readonly opportunityTableCustomization: OpportunityTableCustomizationService,
    private readonly customObjectTableCustomization: CustomObjectTableCustomizationService,
    @Optional()
    @Inject(TaskTableCustomizationServiceToken)
    private readonly taskTableCustomization?: ActivityTableCustomizationService,
  ) {}

  private customizeFilters(
    filters: GalaxyFilterDefinitionInterface[],
    objectType: ObjectType,
    activityType?: ActivityType,
  ): Observable<GalaxyFilterDefinitionInterface[]> {
    switch (objectType) {
      case 'Contact':
        return this.contactTableCustomization.customizeFilters(filters);
      case 'Company':
        return this.companyTableCustomization.customizeFilters(filters);
      case 'Activity':
        switch (activityType) {
          case 'Task':
            return this.taskTableCustomization?.customizeFilters(filters) ?? of(filters);
          default:
            return of(filters);
        }
      case 'Opportunity':
        return this.opportunityTableCustomization.customizeFilters(filters);
      default:
        return of(filters);
    }
  }

  listObjectFilters$(
    searchTerm: string,
    objectType: ObjectType,
    activityType?: ActivityType,
    objectSubtype$?: Observable<string>,
    withNamespace?: string,
  ): Observable<GalaxyFilterDefinitionInterface[]> {
    // Create cache key based on parameters that affect the result
    const createRequestKey = (objectSubtype: string): string => {
      return `${objectType}::${activityType || ''}::${objectSubtype || 'EMPTY'}::${searchTerm}::${withNamespace || ''}`;
    };

    // Share
    if (objectSubtype$) {
      return combineLatest([this.config.namespace$, objectSubtype$]).pipe(
        switchMap(([namespace, objectSubtype]) => {
          const cacheKey = createRequestKey(objectSubtype);

          if (this.filtersRequestMap.has(cacheKey)) {
            return this.filtersRequestMap.get(cacheKey)!;
          }

          const filters$ = this.createFiltersObservable(
            searchTerm,
            objectType,
            activityType,
            namespace,
            objectSubtype,
            withNamespace,
          ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

          this.filtersRequestMap.set(cacheKey, filters$);
          return filters$;
        }),
      );
    }

    // Handle case where objectSubtype$ is not provided
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        const cacheKey = createRequestKey('');

        if (this.filtersRequestMap.has(cacheKey)) {
          return this.filtersRequestMap.get(cacheKey)!;
        }

        const filters$ = this.createFiltersObservable(
          searchTerm,
          objectType,
          activityType,
          namespace,
          '',
          withNamespace,
        ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

        this.filtersRequestMap.set(cacheKey, filters$);
        return filters$;
      }),
    );
  }

  private fetchCustomizedFilters(
    searchTerm: string,
    objectType: ObjectType,
    activityType: ActivityType | undefined,
    namespace: string,
    objectSubtype: string,
    withNamespace?: string,
  ): Observable<GalaxyFilterDefinitionInterface[]> {
    return this.crmObjectService
      .listObjectsFilters(
        objectType,
        {
          namespace: namespace,
          paging: {
            pageSize: FILTER_PAGE_SIZE,
          },
          searchTerm: searchTerm,
          crmObjectSubtype: objectSubtype,
        },
        withNamespace,
      )
      .pipe(
        map((resp) => {
          if (!resp.filters) {
            return [] as GalaxyFilterDefinitionInterface[];
          }
          return resp.filters.map((filterDef) => ({
            fieldId: filterDef.fieldId || '',
            type: convertToGalaxyFilterType(filterDef.typeV2 || FilterType.FILTER_TYPE_INVALID),
            hasFilterOptions: filterDef.hasFilterOptions || false,
            supportedOperators: convertToGalaxyFilterOperators(filterDef.supportedOperatorsV2 || []),
            fieldName: filterDef.fieldName || '',
          }));
        }),
        switchMap((filters) => this.customizeFilters(filters, objectType, activityType)),
      );
  }

  private getFullFilters(
    objectType: ObjectType,
    activityType: ActivityType | undefined,
    namespace: string,
    objectSubtype: string,
    withNamespace?: string,
  ): Observable<GalaxyFilterDefinitionInterface[]> {
    const cacheKey = `${objectType}::${activityType || ''}::${namespace}::${objectSubtype || 'EMPTY'}::${withNamespace || ''}`;
    if (!this.fullFiltersRequestMap.has(cacheKey)) {
      this.fullFiltersRequestMap.set(
        cacheKey,
        this.fetchCustomizedFilters('', objectType, activityType, namespace, objectSubtype, withNamespace).pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
        ),
      );
    }
    return this.fullFiltersRequestMap.get(cacheKey)!;
  }

  private createFiltersObservable(
    searchTerm: string,
    objectType: ObjectType,
    activityType: ActivityType | undefined,
    namespace: string,
    objectSubtype: string,
    withNamespace?: string,
  ): Observable<GalaxyFilterDefinitionInterface[]> {
    if (objectType === 'CustomObject' && !objectSubtype) {
      return of([] as GalaxyFilterDefinitionInterface[]);
    }

    // allFilters is cached per object type/namespace so display-name matching below doesn't
    // refetch the full catalog on every keystroke; it's also already sorted in catalog order.
    const allFilters$ = this.getFullFilters(objectType, activityType, namespace, objectSubtype, withNamespace);

    if (!searchTerm) {
      return allFilters$;
    }

    const backendMatches$ = this.fetchCustomizedFilters(
      searchTerm,
      objectType,
      activityType,
      namespace,
      objectSubtype,
      withNamespace,
    );

    return combineLatest([backendMatches$, allFilters$]).pipe(
      map(([backendMatches, allFilters]) => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        const backendMatchedIds = new Set(backendMatches.map((f) => f.fieldId));
        return allFilters.filter(
          (f) => backendMatchedIds.has(f.fieldId) || f.fieldName?.toLowerCase().includes(lowerSearchTerm),
        );
      }),
    );
  }

  async getInitialAppliedFilters$(
    objectType: ObjectType,
    objectSubtype$?: Observable<string>,
    activityType?: ActivityType,
    useUrlFilters?: boolean,
    withNamespace?: string,
  ): Promise<GalaxyFilterInterface[]> {
    if (objectType === 'Activity' && activityType === 'Task') {
      const urlFilters = firstValueFrom(this.initialFilters$);
      return urlFilters.then((filters) => {
        if (filters?.length > 0 && useUrlFilters) return filters;
        if (this.taskTableCustomization?.getInitialFilters()) {
          return this.taskTableCustomization.getInitialFilters();
        }
        console.error('No getInitialFilters method found in taskTableCustomization service');
        return [];
      });
    }

    if (objectType === 'Activity') {
      const urlFilters = await firstValueFrom(this.initialFilters$);
      const initialActivityFilters = await this.activityCustomizationService.getInitialFilters(
        this.listObjectFilters$('', objectType, undefined, undefined, withNamespace),
      );
      if (urlFilters?.length > 0 && useUrlFilters) {
        return deduplicateFilters([...urlFilters, ...initialActivityFilters]);
      }
      return initialActivityFilters;
    }
    if (objectType === 'Contact') {
      const urlFilters = await firstValueFrom(this.initialFilters$);
      const initialContactFilters = await this.contactTableCustomization.getInitialFilters(
        this.listObjectFilters$('', objectType, undefined, undefined, withNamespace),
      );
      if (urlFilters?.length > 0 && useUrlFilters) {
        return deduplicateFilters([...urlFilters, ...initialContactFilters]);
      }
      return initialContactFilters;
    }
    if (objectType === 'Company') {
      const urlFilters = await firstValueFrom(this.initialFilters$);
      const initialCompanyFilters = await this.companyTableCustomization.getInitialFilters(
        this.listObjectFilters$('', objectType, undefined, undefined, withNamespace),
      );
      if (urlFilters?.length > 0 && useUrlFilters) {
        return deduplicateFilters([...urlFilters, ...initialCompanyFilters]);
      }
      return initialCompanyFilters;
    }
    if (objectType === 'Opportunity') {
      const urlFilters = await firstValueFrom(this.initialFilters$);
      const initialOpportunityFilters = await this.opportunityTableCustomization.getInitialFilters(
        this.listObjectFilters$('', objectType, undefined, undefined, withNamespace),
      );
      if (urlFilters?.length > 0 && useUrlFilters) {
        return deduplicateFilters([...urlFilters, ...initialOpportunityFilters]);
      }
      return initialOpportunityFilters;
    }
    if (objectType === 'CustomObject') {
      const urlFilters = await firstValueFrom(this.initialFilters$);
      const initialCustomObjectFilters = await this.customObjectTableCustomization.getInitialFilters(
        this.listObjectFilters$('', objectType, undefined, objectSubtype$, withNamespace),
      );
      if (urlFilters?.length > 0 && useUrlFilters) {
        return deduplicateFilters([...urlFilters, ...initialCustomObjectFilters]);
      }
      return initialCustomObjectFilters;
    }
    return firstValueFrom(this.initialFilters$);
  }

  setInitialFilters(filters: GalaxyFilterInterface[]) {
    this.initialFilters$$.next(filters);
  }
}
